import { useCallback, useEffect, useRef } from "react";

export interface AudioConfig {
  /** Audio source URL */
  src: string;
  /** Whether to loop the audio */
  loop?: boolean;
  /** Base volume level 0-1 */
  volume?: number;
}

export interface UseAudioOptions {
  /** Audio configuration or URL string */
  audio?: string | AudioConfig;
  /** Whether audio should be playing (e.g., channel is active) */
  isActive?: boolean;
  /** TV volume level (0-40 scale) */
  tvVolume?: number;
  /** TV max volume for normalization (default: 40) */
  tvMaxVolume?: number;
  /** Whether the TV is powered on */
  isPoweredOn?: boolean;
  /** Default loop behavior if not specified in config */
  defaultLoop?: boolean;
  /** Fade duration in ms when starting/stopping (default: 150) */
  fadeDuration?: number;
}

/**
 * Normalizes audio config from string or object format
 */
function normalizeAudioConfig(
  audio: string | AudioConfig | undefined,
  defaultLoop: boolean,
): AudioConfig | null {
  if (!audio) return null;

  if (typeof audio === "string") {
    return { src: audio, loop: defaultLoop, volume: 1 };
  }

  return {
    src: audio.src,
    loop: audio.loop ?? defaultLoop,
    volume: audio.volume ?? 1,
  };
}

/**
 * Hook for managing audio playback in the CRT system.
 * Handles volume integration with TV controls, power state, and fade transitions.
 *
 * @example
 * ```tsx
 * // Channel audio - plays only when channel is active
 * useAudio({
 *   audio: "/sounds/channel-bg.mp3",
 *   isActive: isChannelActive,
 *   tvVolume: volume,
 *   isPoweredOn: isPoweredOn,
 * });
 *
 * // Root audio - always plays when TV is on
 * useAudio({
 *   audio: { src: "/sounds/ambient.mp3", loop: true, volume: 0.5 },
 *   isActive: true,
 *   tvVolume: volume,
 *   isPoweredOn: isPoweredOn,
 *   defaultLoop: true,
 * });
 * ```
 */
export function useAudio({
  audio,
  isActive = true,
  tvVolume = 20,
  tvMaxVolume = 40,
  isPoweredOn = true,
  defaultLoop = false,
  fadeDuration = 150,
}: UseAudioOptions) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const fadeIntervalRef = useRef<number | null>(null);
  const targetVolumeRef = useRef(0);

  const config = normalizeAudioConfig(audio, defaultLoop);

  // Calculate the effective volume (TV volume × base volume)
  const effectiveVolume =
    config && isPoweredOn && isActive
      ? (tvVolume / tvMaxVolume) * (config.volume ?? 1)
      : 0;

  // Clean up fade interval
  const clearFade = useCallback(() => {
    if (fadeIntervalRef.current !== null) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
  }, []);

  // Fade volume to target
  const fadeToVolume = useCallback(
    (target: number, onComplete?: () => void) => {
      const audioEl = audioRef.current;
      if (!audioEl) {
        onComplete?.();
        return;
      }

      clearFade();
      targetVolumeRef.current = target;

      const startVolume = audioEl.volume;
      const volumeDiff = target - startVolume;

      if (Math.abs(volumeDiff) < 0.01) {
        audioEl.volume = target;
        onComplete?.();
        return;
      }

      const steps = Math.max(1, Math.floor(fadeDuration / 16)); // ~60fps
      const stepSize = volumeDiff / steps;
      let currentStep = 0;

      fadeIntervalRef.current = window.setInterval(() => {
        currentStep++;
        if (currentStep >= steps) {
          audioEl.volume = target;
          clearFade();
          onComplete?.();
        } else {
          audioEl.volume = Math.max(
            0,
            Math.min(1, startVolume + stepSize * currentStep),
          );
        }
      }, 16);
    },
    [fadeDuration, clearFade],
  );

  // Initialize or update audio element when config changes
  useEffect(() => {
    if (!config) {
      // No audio config - clean up existing audio
      if (audioRef.current) {
        clearFade();
        audioRef.current.pause();
        audioRef.current = null;
      }
      return;
    }

    // Create new audio element if needed or src changed
    if (!audioRef.current || audioRef.current.src !== config.src) {
      if (audioRef.current) {
        clearFade();
        audioRef.current.pause();
      }

      const audioEl = new Audio(config.src);
      audioEl.loop = config.loop ?? false;
      audioEl.volume = 0; // Start silent, will fade in
      audioRef.current = audioEl;
    } else {
      // Update loop setting if it changed
      audioRef.current.loop = config.loop ?? false;
    }

    return () => {
      clearFade();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [config?.src, config?.loop, clearFade]);

  // Handle playback state and volume changes
  useEffect(() => {
    const audioEl = audioRef.current;
    if (!audioEl || !config) return;

    const shouldPlay = isPoweredOn && isActive && effectiveVolume > 0;

    if (shouldPlay) {
      // Start playing if not already
      if (audioEl.paused) {
        audioEl.volume = 0;
        audioEl.play().catch(() => {
          // Autoplay may be blocked - that's okay, user interaction will start it
        });
      }
      fadeToVolume(effectiveVolume);
    } else {
      // Fade out then pause
      fadeToVolume(0, () => {
        audioEl.pause();
        // Reset to beginning if not looping and power went off
        if (!isPoweredOn && !config.loop) {
          audioEl.currentTime = 0;
        }
      });
    }
  }, [config, isPoweredOn, isActive, effectiveVolume, fadeToVolume]);

  // Return controls for manual audio management if needed
  return {
    play: useCallback(() => {
      audioRef.current?.play().catch(() => {});
    }, []),
    pause: useCallback(() => {
      audioRef.current?.pause();
    }, []),
    setCurrentTime: useCallback((time: number) => {
      if (audioRef.current) {
        audioRef.current.currentTime = time;
      }
    }, []),
  };
}
